import { gql, useQuery } from "@apollo/client";
import { ResponsiveCirclePacking } from "@nivo/circle-packing";
import {
  CirclesLoader,
  LineChart,
  Shimmers,
  StatsPageLoader,
  TableBodyComponent,
} from "components";
import { ComplaintCategoryPicker } from "containers";
import lodash from "lodash";
import moment from "moment";
import numeral from "numeral";
import { FC, useMemo } from "react";
import { useNavigate, useSearch } from "react-location";
import { LocationGenerics } from "router/location";
import { classNames, getPeriod, useSummaryData, useUrlState } from "utils";

const DATE_FORMAT = "YYYY-MM-DD";
const SUMMARY = gql`
  query GetSummary($fromDate: Date, $toDate: Date, $region: ID, $district: ID) {
    rows: getSummary(
      fromDate: $fromDate
      toDate: $toDate
      region: $region
      district: $district
    ) {
      _id
      date
      region {
        _id
        code
        name
      }
      district {
        _id
        code
        name
      }
      newCustomers
      newCustomersIndividual
      newCustomersOrganization
      newCustomersPostpaid
      newCustomersPrepaid
      newCustomersAMR
      newCustomersResidential
      newCustomersNonResidential
      newCustomersIndustrial
      newCustomersStandard
      newCustomersExpress
      newCustomersPremium
      newCustomersTariffClasses
      lateSetups
      lateSetupsIndividual
      lateSetupsOrganization
      lateSetupsPostpaid
      lateSetupsPrepaid
      lateSetupsAMR
      lateSetupsResidential
      lateSetupsNonResidential
      lateSetupsIndustrial
      lateSetupsShep
      lateSetupsNotMigrated
      lateSetupsRegularization
      lateSetupsTariffClasses
      replacementsPostpaidToPrepaid
      replacementsPrepaidToPrepaid
      replacementsPostpaidToPostpaid
      replacementsPrepaidToPostpaid
      replacementsPostpaidToAMR
      replacementsPrepaidToAMR
      replacementsAMRToAMR
      replacementsAMRToPrepaid
      replacementsAMRToPostpaid
      complaintsNew
      complaintsResolved
      complaintsInProgress
      complaintsCancelled
      newReadings
      newReadingExceptions
      newReadingAnomalies
      newMeterEngagements
      newNoTraces
      newNoAccesses
      newUnlistedMeters
      consumptionBilled
      amountBilled
    }
  }
`;

const GET_COMPLAINT_TYPES_SUMMARY = gql`
  query GetComplaintTypesSummary(
    $region: ID
    $district: ID
    $category: ComplaintCategory
  ) {
    complaintTypes: getComplaintTypesSummary(
      region: $region
      district: $district
      category: $category
    ) {
      totalComplaints
      complaintType {
        name
        _id
      }
    }
  }
`;

const CustomerComplaintsSummaryPage: FC = () => {
  const searchParams = useSearch<LocationGenerics>();
  const [complaintCategory, setComplaintCategory] = useUrlState(
    "complaintCategory",
    "General",
  );
  const navigate = useNavigate();

  const filter = useMemo(
    () => ({
      region: searchParams.region || undefined,
      district: searchParams.district || undefined,
      fromDate: moment(searchParams.fromDate, DATE_FORMAT)
        .startOf("day")
        .toDate(),
      toDate: moment(searchParams.toDate, DATE_FORMAT).startOf("day").toDate(),
    }),
    [searchParams],
  );

  const { data: complaintTypesData, loading: complaintTypesLoading } = useQuery(
    GET_COMPLAINT_TYPES_SUMMARY,
    {
      variables: {
        region: searchParams.region || undefined,
        district: searchParams.district || undefined,
        category: complaintCategory,
      },
    },
  );
  const { data, loading, networkStatus } = useQuery(SUMMARY, {
    variables: filter,
    notifyOnNetworkStatusChange: false,
  });

  const { keyedGraphData, records, totals } = useSummaryData(data);

  const stats = useMemo(
    () => [
      {
        id: "new",
        name: "New",
        value: lodash.get(totals, "complaintsNew"),
      },
      {
        id: "resolved",
        name: "Resolved",
        value: lodash.get(totals, "complaintsResolved"),
      },
      {
        id: "cancelled",
        name: "Cancelled",
        value: lodash.get(totals, "complaintsCancelled"),
      },
      {
        id: "inProgress",
        name: "In Progress",
        value: lodash.get(totals, "complaintsInProgress"),
      },
    ],
    [totals],
  );

  if (loading) {
    return <StatsPageLoader />;
  }

  return (
    <div className="flex-1 min-h-full min-w-0  py-4 sm:py-6 px-6 overflow-hidden overflow-y-auto light flex flex-col">
      <div className="">
        <div className="flex flex-col">
          <h3 className="text-md font-medium leading-6 text-gray-900">
            Summary
          </h3>
          <div className="mt-3 grid grid-cols-1 gap-5 sm:grid-cols- lg:grid-cols-2">
            <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2">
              {stats.map((item) => (
                <div
                  key={item.id}
                  className="relative overflow-hidden rounded-lg bg-white p-4 shadow"
                >
                  <dt>
                    <p className="truncate text-base font-medium text-gray-500">
                      {item.name}
                    </p>
                  </dt>
                  <dd className="flex justify-between mt-2 items-end">
                    <p className="text-4xl font-light print:font-bold  text-gray-900">
                      {numeral(item.value).format("0,0")}
                    </p>
                  </dd>
                </div>
              ))}
            </dl>
            <dl>
              <div className="bg-white rounded-lg col-span-1  shadow p-5  flex flex-col  relative">
                <div className="flex  justify-between items-start">
                  <h3 className="text-md font-medium leading-6 text-gray-900">
                    Complaints By Type
                    <p className="text-xs text-gray-400">
                      Shows distribution of complaints by type per category
                    </p>
                  </h3>
                  <ComplaintCategoryPicker
                    id="complaintCategory"
                    values={{ complaintCategory }}
                    setFieldValue={(_: any, value: string) => {
                      setComplaintCategory(value);
                    }}
                    rawId
                  />
                </div>
                <div className={classNames("h-80 w-96 mx-auto")}>
                  {complaintTypesLoading ? (
                    <CirclesLoader />
                  ) : (
                    <ResponsiveCirclePacking
                      data={{
                        children:
                          complaintTypesData?.complaintTypes?.map(
                            (item: any) => ({
                              ...item,
                              id: item?.complaintType?._id || "N/A",
                              label: item?.complaintType?.name || "N/A",
                              value: item?.totalComplaints || 0,
                            }),
                          ) || [],
                      }}
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                      valueFormat={(value) =>
                        `${numeral(value).format("0,0")} complaints`
                      }
                      colorBy="id"
                      onClick={(node) => {
                        navigate({
                          search: (prev) => ({
                            ...prev,
                            complaintType: node?.id,
                            tab: "complaints-data",
                          }),
                        });
                      }}
                      tooltip={({ id }) => (
                        <div
                          className="min-w-max"
                          style={{
                            padding: "8px",
                            background: "white",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          <div>
                            <strong>
                              {
                                complaintTypesData?.complaintTypes?.find(
                                  (item: any) =>
                                    item?.complaintType?._id === id,
                                )?.complaintType?.name
                              }
                            </strong>
                            :{" "}
                            {numeral(
                              complaintTypesData?.complaintTypes?.find(
                                (item: any) => item?.complaintType?._id === id,
                              )?.totalComplaints,
                            ).format("0,0")}
                          </div>
                        </div>
                      )}
                      padding={5}
                      enableLabels={false}
                      leavesOnly={true}
                      labelsSkipRadius={10}
                      labelTextColor={"white"}
                      // tooltip={tooltipLabel}
                      borderWidth={1}
                      borderColor={{
                        from: "color",
                        modifiers: [["darker", 0.5]],
                      }}

                      // label={chartLabel}
                    />
                  )}
                </div>
              </div>
            </dl>
          </div>
        </div>
      </div>
      {!searchParams.district && (
        <div className="flex flex-row mt-6 space-x-6">
          <div className="flex-1">
            <div className="flex flex-col">
              <div className="overflow-y-auto">
                <TableBodyComponent
                  title={`Complaints Per Status Per ${
                    searchParams?.region ? "District" : "Region"
                  }`}
                  exportFileName={`Complaints By Status  Per ${
                    searchParams?.region ? "District" : "Region"
                  } For ${getPeriod(searchParams)}`}
                  exportTypes={["EXCEL", "PDF"]}
                  noSpace={true}
                  loading={loading && ![4, 6].includes(networkStatus)}
                  data={{
                    ...records,
                    rows: [
                      ...records.rows,
                      {
                        ...totals,
                        _id: "total",
                        region: {
                          _id: "total",
                          code: "total",
                          name: "Total",
                        },
                        district: {
                          _id: "total",
                          code: "total",
                          name: "Total",
                        },
                      },
                    ],
                  }}
                  renderColumns={() => (
                    <>
                      <tr className="hidden print:table-row">
                        <th
                          scope="col"
                          colSpan={2}
                          className="sticky top-0 border-t border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-center text-md font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                        >
                          Complaints By Status Per{" "}
                          {searchParams?.region ? "District" : "Region"} For{" "}
                          {getPeriod(searchParams)}
                        </th>
                      </tr>
                      <tr>
                        {!!searchParams.region ? (
                          <th
                            scope="col"
                            className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                          >
                            District
                          </th>
                        ) : (
                          <th
                            scope="col"
                            className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                          >
                            Region
                          </th>
                        )}
                        <th
                          scope="col"
                          className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-right text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                        >
                          New
                        </th>
                        <th
                          scope="col"
                          className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-right text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                        >
                          Resolved
                        </th>
                        <th
                          scope="col"
                          className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-right text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                        >
                          Cancelled
                        </th>
                        <th
                          scope="col"
                          className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-right text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                        >
                          In Progress
                        </th>
                      </tr>
                    </>
                  )}
                  renderLoader={() => (
                    <tr className="border-t border-gray-200 print:border-gray-700">
                      <td className="px-4 py-4 border-b border-gray-200 print:border-gray-700">
                        <Shimmers.SingleShimmer />
                      </td>
                      <td className="px-4 py-4 border-b border-gray-200 print:border-gray-700">
                        <Shimmers.SingleShimmer />
                      </td>
                      <td className="px-4 py-4 border-b border-gray-200 print:border-gray-700">
                        <Shimmers.SingleShimmer />
                      </td>
                      <td className="px-4 py-4 border-b border-gray-200 print:border-gray-700">
                        <Shimmers.SingleShimmer />
                      </td>
                      <td className="px-4 py-4 border-b border-gray-200 print:border-gray-700">
                        <Shimmers.SingleShimmer />
                      </td>
                    </tr>
                  )}
                  renderItem={(item) => (
                    <tr
                      key={item._id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    >
                      {!!searchParams.region ? (
                        <td className="whitespace-nowrap px-4 py-4 text-normal text-gray-700 border-b border-gray-200 print:border-gray-700">
                          {item?.district?.name || "N/A"}
                        </td>
                      ) : (
                        <td className="whitespace-nowrap px-4 py-4 text-normal text-gray-700 border-b border-gray-200 print:border-gray-700">
                          {item?.region?.name || "N/A"}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-4 py-4 text-normal text-right text-gray-700 border-b border-gray-200 print:border-gray-700">
                        {numeral(item?.complaintsNew).format("0,0") || "N/A"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-normal text-right text-gray-700 border-b border-gray-200 print:border-gray-700">
                        {numeral(item?.complaintsResolved).format("0,0") ||
                          "N/A"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-normal text-right text-gray-700 border-b border-gray-200 print:border-gray-700">
                        {numeral(item?.complaintsCancelled).format("0,0") ||
                          "N/A"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-normal text-right text-gray-700 border-b border-gray-200 print:border-gray-700">
                        {numeral(item?.complaintsInProgress).format("0,0") ||
                          "N/A"}
                      </td>
                    </tr>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6">
        <LineChart
          title="Complaints Per Status Per Day"
          exportFileName={`Complaints Per Status For ${getPeriod(
            searchParams,
          )}`}
          label="Status"
          loading={loading && ![4, 6].includes(networkStatus)}
          keys={[
            {
              color: "#2563eb",
              bgColor: "bg-blue-600",
              id: "New",
              label: "New",
            },
            {
              color: "#0d9488",
              bgColor: "bg-teal-600",
              id: "Resolved",
              label: "Resolved",
            },
            {
              color: "#9333ea",
              bgColor: "bg-purple-600",
              id: "InProgress",
              label: "In Progress",
            },
            {
              color: "#9333ea",
              bgColor: "bg-purple-600",
              id: "Cancelled",
              label: "Cancelled",
            },
          ]}
          data={lodash.times(
            moment(searchParams.toDate, DATE_FORMAT).diff(
              moment(searchParams.fromDate, DATE_FORMAT),
              "days",
            ) + 1,
            (idx) => {
              const currentDate = moment(searchParams.fromDate)
                .add(idx, "days")
                .format(DATE_FORMAT);
              return {
                value: {
                  New: lodash.get(
                    keyedGraphData,
                    `${currentDate}.complaintsNew`,
                    0,
                  ),
                  Resolved: lodash.get(
                    keyedGraphData,
                    `${currentDate}.complaintsResolved`,
                    0,
                  ),
                  InProgress: lodash.get(
                    keyedGraphData,
                    `${currentDate}.complaintsInProgress`,
                    0,
                  ),
                  Cancelled: lodash.get(
                    keyedGraphData,
                    `${currentDate}.complaintsCancelled`,
                    0,
                  ),
                },
                day: currentDate,
              };
            },
          )}
        />
      </div>
    </div>
  );
};

export default CustomerComplaintsSummaryPage;
